'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2 } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { QR_REVIEW_HASH } from '../constants';
import type { QrReviewBatch } from '../types';

interface QrReviewTableActionBarProps {
  table: Table<QrReviewBatch>;
}

export function QrReviewTableActionBar({ table }: QrReviewTableActionBarProps) {
  const t = useTranslations('qrReview');
  const { open: deleteOpen, onOpenChange: toggleDelete } = useHashDialog(
    QR_REVIEW_HASH.bulkDelete
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const displayCount = selectedRows.length;

  async function handleBulkDelete() {
    setIsDeleting(true);
    try {
      // Bulk delete is a placeholder — no API endpoint exists yet
      table.resetRowSelection();
    } catch {
      // handled by global MutationCache
    } finally {
      setIsDeleting(false);
    }
    toggleDelete(false);
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleDelete(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={toggleDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('bulkDeleteConfirm', { count: displayCount })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {isDeleting && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
              {t('bulkDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
